import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { authManager } from 'lib-shared-auth';
import { config as appConfig } from 'lib-shared-backend-config';

import { axiosInstance } from 'app/app.api';

/** Represents shorthand notations for the base path. */
export enum ApiBasePath {
  Rest,
  Files,
}

/** Represents the error status codes. */
export enum ApiErrorStatusCode {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  InternalServerError = 500,
  BadGateway = 502,
  ServiceUnavailable = 503,
}

/** Represents the request method. */
export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

/** Represents the response type. */
export type ApiResponseType = 'json' | 'text' | 'blob';

/** Represents the content type. */
export type ApiContentType =
  | 'application/json'
  | 'multipart/form-data'
  | (string & {});

/**
 * Represents the api headers. Note: To omit an automatically added header set
 * it to null.
 */
export interface ApiHeaders {
  readonly contentType?: ApiContentType | null;
  readonly authorization?: string | null;
}

/** Represents an api request. */
export interface ApiRequest {
  /** The absolute or relative path. */
  readonly path: string;
  /** The method. */
  readonly method?: ApiMethod;
  /** The headers. */
  readonly headers?: ApiHeaders;
  /** The payload data. */
  readonly data?: unknown;
  /** The url search parameters. */
  readonly params?: Record<string, string | boolean | number | undefined>;
  /** The type of data the server will respond with. */
  readonly responseType?: ApiResponseType;
  /** Ignores error handling for the status codes. */
  readonly ignoreErrorCodes?: ReadonlyArray<ApiErrorStatusCode>;
  /**
   * The base path that gets prepended on the `path`. Accepts shorthand enum or
   * custom path.
   */
  readonly basePath?: ApiBasePath | string;
}

/** Mappings from shorthand base url names to urls. */
const API_BASE_URL = {
  [ApiBasePath.Rest]: appConfig.url.rest,
  [ApiBasePath.Files]: appConfig.url.files,
};

/** Mapping from consumer friendly header names to what ends in the http headers. */
const CONTENT_TYPE: Record<keyof ApiHeaders, string> = {
  contentType: 'Content-Type',
  authorization: 'Authorization',
};

/**
 * Creates an axios config from the request.
 *
 * @param request The request.
 */
async function createAxiosConfig(
  request: ApiRequest,
): Promise<AxiosRequestConfig> {
  const authData = await authManager.getAuthData();

  const token = authData?.access_token;
  const tokenBase64 = token ? btoa(`token:${token}`) : '';
  const defaultHeaders: ApiHeaders = {
    contentType: 'application/json',
    authorization: `Basic ${tokenBase64}`,
  };

  return {
    url: request.path,
    method: request.method || 'GET',
    baseURL:
      request.basePath != null
        ? typeof request.basePath === 'string'
          ? request.basePath
          : API_BASE_URL[request.basePath]
        : API_BASE_URL[ApiBasePath.Rest],

    headers: Object.entries({
      ...defaultHeaders,
      ...(request.headers || {}),
    }).reduce(
      (headers, [key, value]) =>
        value == null
          ? headers
          : ((headers[CONTENT_TYPE[key]] = value), headers),
      {},
    ),
    ...(request.responseType ? { responseType: request.responseType } : {}),
    ...(request.params ? { params: request.params } : {}),
    ...(request.data ? { data: request.data } : {}),
    validateStatus: (status) => {
      const ignoreErrorCodes = request.ignoreErrorCodes || [];
      if (ignoreErrorCodes.includes(status)) return true;
      return status >= 200 && status < 300; // default
    },
  };
}

/**
 * Sends an ajax request.
 *
 * @deprecated Use RTK Query endpoints where possible.
 * @param request The request.
 */
export async function ajaxSend<TResponse = unknown>(
  request: ApiRequest,
): Promise<TResponse>;
/**
 * Sends an ajax request.
 *
 * @deprecated Use RTK Query endpoints where possible.
 * @param request The request.
 * @param fullResponse Whether or not to return the full response.
 */
export async function ajaxSend<TResponse = unknown>(
  request: ApiRequest,
  fullResponse: boolean,
): Promise<AxiosResponse<TResponse>>;
export async function ajaxSend<TResponse = unknown>(
  request: ApiRequest,
  returnFullResponse = false,
): Promise<TResponse | AxiosResponse<TResponse>> {
  const config = await createAxiosConfig(request);

  try {
    const response = await axiosInstance.request<TResponse>(config);
    return returnFullResponse ? response : response.data;
  } catch (error) {
    const exception = error as AxiosError;
    return Promise.reject(exception);
  }
}
